import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Figure, Timeline } from '../components/Diagram';

interface Row {
  key: string;
  name: string;
  blurb: string;
}

const STEPS: Row[] = [
  {
    key: 'author',
    name: 'Author the version that used to exist',
    blurb:
      'Open the product in your catalog — a bundle, a module or a usage component — go to its version history and choose Add a past version, then give it the date it took effect. Revvly copies the version that was in force closest to that date, so you start from something real rather than a blank form. Edit the prices to what the plan actually cost then, and publish.',
  },
  {
    key: 'archived',
    name: 'It publishes as a past version, not the current offer',
    blurb:
      'A past version lands Archived: it holds a closed window in the plan’s history. Your current version is untouched, still what new customers get, still what your pricing pages describe. Nothing changes for anyone already subscribed.',
  },
  {
    key: 'assign',
    name: 'Put the client on it',
    blurb:
      'A new client: show retired plans in the picker and choose the version. An existing client: open Migrate and pick it from the list. Either way Revvly asks why, because a client on a plan you no longer sell is a decision someone made, and the next person to look needs to know it was deliberate.',
  },
  {
    key: 'bills',
    name: 'They bill at that version’s prices, indefinitely',
    blurb:
      'Not a discount, not an override — the subscription is pinned to that version, and its invoices, renewals and MRR all read from it. Publishing new versions of the plan will not move them. Only an explicit migration will.',
  },
];

const RULES: Row[] = [
  {
    key: 'order',
    name: 'Version numbers are the order you created them, not the order they applied',
    blurb:
      'A version you author today for 2023 gets the next free number — so it can be your highest-numbered version while being the oldest in time. Read the dates. Every screen orders by the window a version was in force for, not by its number.',
  },
  {
    key: 'before',
    name: 'A past version has to take effect before your current one',
    blurb:
      'Otherwise it would be the version in force today, while being marked as retired — and imports covering this month would attach to it. Revvly refuses that date and tells you which version is in the way.',
  },
  {
    key: 'reason',
    name: 'A reason is always required',
    blurb:
      'It is what separates a client you deliberately kept on an old plan from a subscription nobody got round to updating. The first is a contract to honour; the second is work to do. Revvly shows the reason on the client and leaves them out of the outdated-pins list.',
  },
  {
    key: 'nomoney',
    name: 'Recording history moves no money',
    blurb:
      'Reconstructing what a client used to be on writes history, not charges. It raises no invoice and adjusts nothing. Invoices for past periods come from backfilling them, which is a separate, explicit choice.',
  },
];

export function LegacyPricingGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Price a client on an older version — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Price a client on an older version</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Some customers are on a plan you <em>stopped selling</em>. Keep them there,
          on purpose.
        </h1>
        <p className="dp-oneliner">
          Create the version of a product as it was — last year&rsquo;s prices, an older
          set of modules, a metering model you have since changed — and put a client on
          it. For customers who predate Revvly, or who negotiated the plan you were
          selling at the time.
        </p>
      </header>

      <section className="dp-section">
        <h2>When you need this</h2>
        <p className="dp-plain">
          Two situations, one mechanism. <strong>You are moving a customer into
          Revvly</strong> who has been with you for years: the plan they bought no
          longer exists in your catalog, and typing today&rsquo;s price would restate
          their whole history at a number they never paid. Or <strong>you are keeping a
          customer where they are</strong>: you raised prices, and this account stays on
          the old ones because that is what you agreed.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Both need the same thing — the older version has to <em>exist</em> in the
          catalog before a subscription can point at it. That is what this guide covers.
          If you only need to stop an existing customer moving forward when you publish
          a new version, you do not need any of this: that is already how Revvly
          behaves, and it is described in{' '}
          <Link to="/docs/guides/versioning">Versioning &amp; grandfathering</Link>.
        </p>
      </section>

      <section className="dp-section">
        <h2>How it goes</h2>
        <ol className="qs-steps">
          {STEPS.map((s, i) => (
            <li className="qs-step" key={s.key}>
              <span className="qs-phase-num" style={{ marginRight: 12 }}>
                {i + 1}
              </span>
              <div>
                <div className="qs-step-label">{s.name}</div>
                <div className="qs-step-detail">{s.blurb}</div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="dp-section">
        <h2>What you can author a past version of</h2>
        <p className="dp-plain">
          Everything in your catalog that carries a price. Each keeps its own version
          history, and each is authored the same way — open it in the catalog, go to
          version history, choose <strong>Add a past version</strong>. The button is on
          the version-history screen, not the editor.
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Bundle</div>
            <div className="dp-field-v">
              The packaged plan you sell — <em>Growth</em>, <em>Pro</em>. A past version
              carries the base price it charged then, per currency and per region. This
              is the usual one: most legacy contracts are &ldquo;they are on the old
              Growth price&rdquo;.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Module</div>
            <div className="dp-field-v">
              An individual product a client can hold on its own. A past version carries
              its price and the usage components attached to it, so a plan whose metering
              you have since restructured can be reconstructed as it was, not only
              repriced.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Usage component</div>
            <div className="dp-field-v">
              What you meter and how. A past version carries its rates and its{' '}
              <em>structure</em> — tier boundaries, included units, the metering model —
              so a plan you have since restructured can be reconstructed as it was, not
              merely repriced.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>Reconstructing a whole history</h2>
        <p className="dp-plain">
          A customer who has been with you since 2023 was probably not on one plan the
          entire time. When you create their subscription you can set the start date
          back to when they actually signed and add each plan change since — the date it
          happened and what they moved to.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Revvly then treats their history as a sequence of periods rather than a single
          current plan. If you also ask it to backfill the invoices for those elapsed
          terms, <strong>each invoice is billed at the plan that was in force for its own
          period</strong> — the 2023 months at the 2023 price, under the 2023
          plan&rsquo;s name — and the revenue reports show the change where it actually
          happened, not all at once today.
        </p>
        <Figure
          title="One client, several plans"
          caption="Each backfilled invoice bills at the plan in force for its own period."
        >
          <Timeline
            items={[
              { when: 'Start date', title: 'Signed on the first plan', text: 'Those periods bill at that plan\u2019s price and name.', tone: 'muted' },
              { when: 'Each plan change', title: 'Moved to the next plan', text: 'Entered oldest first, each in the past.' },
              { when: 'Latest change', title: 'Current plan', text: 'Later periods bill at its price.', tone: 'accent' },
            ]}
          />
        </Figure>
        <div className="api-note" style={{ marginTop: 18 }}>
          <span className="api-note-icon">i</span>
          <span>
            Changes must be entered oldest first, each after the subscription started and
            each in the past. Revvly checks the whole list before writing any of it, so a
            mistake in the third step does not leave you with the first two recorded.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <h2>What you will see afterwards</h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">On the subscription</div>
            <div className="dp-field-v">
              <strong>Legacy plan</strong>, with the reason you gave — rather than a
              warning that the pin is out of date. The client is where you put them, and
              the page says so.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Plan history</div>
            <div className="dp-field-v">
              The periods this client spent on each plan, with dates and the price each
              period billed. A change queued for their next renewal is shown as pending
              rather than as something already applied.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Outdated pins</div>
            <div className="dp-field-v">
              They are <em>not</em> listed. That page is for subscriptions left behind by
              accident; a deliberate one would only be noise there — and it is one bulk
              action away from being repriced. Turn on <em>Show grandfathered</em> to see
              them deliberately.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">On the product</div>
            <div className="dp-field-v">
              Version history lists every version oldest first by when it took effect, so
              the one you authored for 2023 sits at the start of the timeline even though
              you created it today.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>Rules worth knowing</h2>
        <div className="dp-fields-table">
          {RULES.map((r) => (
            <div className="dp-field-row" key={r.key}>
              <div className="dp-field-k">{r.name}</div>
              <div className="dp-field-v">{r.blurb}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="dp-section">
        <h2>Undoing it</h2>
        <p className="dp-plain">
          To move a grandfathered client onto your current pricing, migrate them like
          anyone else — choose the current version and a timing. Migrating at the next
          renewal is the usual choice: it avoids a mid-cycle adjustment invoice and the
          customer sees the change at a boundary they expect. Their history stays intact
          either way; the periods they spent on the old plan do not disappear when they
          leave it.
        </p>
      </section>

      <section className="dp-section">
        <h2>Next up</h2>
        <ul className="dp-connects">
          <li>
            <Link
              to="/docs/guides/versioning"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Versioning &amp; grandfathering ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/import-invoices"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Import existing invoices ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/mid-cycle-changes"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Handle a mid-cycle change ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
